import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import {
  Search, Users, Stethoscope, Pill, FlaskConical, LayoutGrid,
  CornerDownLeft, Loader2, X,
} from "lucide-react";

const SPECIALTY_ACCENT = {
  nephrology: "#4ADE80",
  cardiology: "#F87171",
  neurology:  "#A78BFA",
  oncology:   "#FBBF24",
  platform:   "#60A5FA",
};

const GROUPS = [
  { key: "patients", label: "Patients",       icon: Users,        hint: "roster" },
  { key: "pages",    label: "Pages · Tools",  icon: LayoutGrid,   hint: "navigate" },
  { key: "icd10",    label: "ICD-10 diagnoses", icon: Stethoscope, hint: "condition" },
  { key: "loinc",    label: "LOINC observations", icon: FlaskConical, hint: "lab code" },
  { key: "rxnorm",   label: "RxNorm medications", icon: Pill,       hint: "drug" },
];

/**
 * GlobalCommandPalette — Cmd/⌘+K unified search.
 *
 * Mounted once at App root, listens for the keyboard shortcut only when an
 * authenticated user is present. Searches patients (provider only), curated
 * ICD-10/LOINC/RxNorm standards, and specialty calculator pages. Arrow-key
 * navigable with Enter to activate; scoped by the current provider specialty
 * where relevant.
 */
export default function GlobalCommandPalette() {
  const { user, setViewingPatient } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const flat = useMemo(() => {
    if (!results) return [];
    const out = [];
    for (const g of GROUPS) {
      const rows = results[g.key] || [];
      for (const r of rows) out.push({ ...r, __group: g.key });
    }
    return out;
  }, [results]);

  // Keyboard shortcut — only when a user is signed in.
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((x) => !x);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    const onEvt = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-command-palette", onEvt);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-command-palette", onEvt);
    };
  }, [user, open]);

  // Reset on close, autofocus on open.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ(""); setResults(null); setActiveIdx(0);
    }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) { setResults(null); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: term, limit: 8 } });
        setResults(data);
        setActiveIdx(0);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const activate = useCallback((row) => {
    if (!row) return;
    setOpen(false);
    if (row.__group === "patients") {
      // Provider clicked a patient row — set viewing patient and jump to dashboard.
      setViewingPatient?.({
        patient_id: row.id, name: row.name, email: row.email,
      });
      navigate("/");
      return;
    }
    if (row.__group === "pages") { navigate(row.route); return; }
    // Standards: jump to the Standards page (search box lives there).
    navigate("/standards");
  }, [navigate, setViewingPatient]);

  const onKeyInInput = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      activate(flat[activeIdx]);
    }
  };

  if (!user || !open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-24 px-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
      onClick={() => setOpen(false)}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-[#272E2B] bg-[#0F1310] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#272E2B]">
          {loading ? (
            <Loader2 size={16} className="text-[#A78BFA] animate-spin" />
          ) : (
            <Search size={16} className="text-[#A3ACA7]" />
          )}
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyInInput}
            placeholder="Search patients, ICD-10, LOINC, RxNorm, calculators…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder-[#7A8480]"
          />
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#272E2B] text-[#A3ACA7]">
            esc
          </span>
          <button
            onClick={() => setOpen(false)}
            className="text-[#7A8480] hover:text-white p-1"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="command-palette-results">
          {!q.trim() && (
            <div className="px-4 py-8 text-center text-sm text-[#A3ACA7]">
              Type to search across every department · standards · patients
              <div className="mt-3 text-[11px] text-[#7A8480]">
                Tip: try <span className="text-white">stroke</span>,{" "}
              <span className="text-white">lisinopril</span>, or a patient name
              </div>
            </div>
          )}

          {q.trim() && !loading && flat.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-[#A3ACA7]">
              No matches for <span className="text-white">&ldquo;{q}&rdquo;</span>
            </div>
          )}

          {GROUPS.map((g) => {
            const rows = results?.[g.key] || [];
            if (!rows.length) return null;
            const Icon = g.icon;
            return (
              <div key={g.key} data-testid={`cp-group-${g.key}`}>
                <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-wider text-[#7A8480] flex items-center gap-2">
                  <Icon size={11} /> {g.label}
                  <span className="text-[#3A403C]">· {g.hint}</span>
                </div>
                {rows.map((r) => {
                  const idx = flat.findIndex(
                    (x) => x.__group === g.key && (x.id === r.id || x.code === r.code || x.route === r.route),
                  );
                  const active = idx === activeIdx;
                  return (
                    <button
                      key={`${g.key}-${r.id || r.code || r.route}`}
                      type="button"
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => activate({ ...r, __group: g.key })}
                      data-testid={`cp-row-${g.key}-${r.id || r.code || r.route}`}
                      className={`w-full text-left px-4 py-2.5 flex items-center gap-3 border-l-2 transition-colors ${
                        active ? "bg-[#1C201E] border-l-[#4ADE80]" : "border-l-transparent hover:bg-[#161A18]"
                      }`}
                    >
                      <CommandRow row={r} group={g.key} />
                      {active && (
                        <span className="ml-auto text-[10px] text-[#A3ACA7] flex items-center gap-1 shrink-0">
                          <CornerDownLeft size={11} /> open
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-[#272E2B] flex items-center justify-between text-[10px] text-[#7A8480]">
          <div>
            <span className="text-white">↑ ↓</span> navigate ·{" "}
            <span className="text-white">↵</span> open ·{" "}
            <span className="text-white">esc</span> close
          </div>
          <div>Med Nexus · Global search</div>
        </div>
      </div>
    </div>
  );
}

function CommandRow({ row, group }) {
  if (group === "patients") {
    return (
      <div className="min-w-0">
        <div className="text-sm text-white font-medium truncate">{row.name}</div>
        <div className="text-[11px] text-[#7A8480] truncate">{row.email}</div>
      </div>
    );
  }
  if (group === "pages") {
    const accent = SPECIALTY_ACCENT[row.specialty] || "#A3ACA7";
    return (
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate">{row.title}</div>
        <div className="text-[11px] uppercase tracking-wider mt-0.5" style={{ color: accent }}>
          {row.specialty}
        </div>
      </div>
    );
  }
  // Standards row (icd10 / loinc / rxnorm)
  const accent = SPECIALTY_ACCENT[row.specialty] || "#A3ACA7";
  return (
    <div className="min-w-0 flex-1">
      <div className="text-sm text-white truncate">
        <span className="font-mono text-[11px] text-[#A3ACA7] mr-2">{row.code}</span>
        {row.display}
      </div>
      <div className="text-[11px] mt-0.5 flex items-center gap-2">
        {row.specialty && (
          <span className="uppercase tracking-wider" style={{ color: accent }}>
            {row.specialty}
          </span>
        )}
        {row.unit && <span className="text-[#7A8480]">· {row.unit}</span>}
      </div>
    </div>
  );
}
